import { IS_GLOBALPAY_PAYMENT, IS_TRANSIT_PAYMENT } from 'services/api/constants';
import {
  AGING_DATE_BASIS,
  AgingDateBasis,
  BaseApiLegalCorporationConfig,
  DEFAULT_AGING_BUCKETS,
  DEFAULT_AGING_DATE_BASIS,
  DEFAULT_PAYMENT_LIMITS,
  PAYMENT_METHODS,
  PaymentMethod,
  UpdateSettingsRequestBody,
} from 'services/api/legalCorporationConfig/types';
import * as yup from 'yup';

// Payment sections only apply when a payment provider is active
export const SHOW_PAYMENT_SETTINGS = Boolean(IS_GLOBALPAY_PAYMENT || IS_TRANSIT_PAYMENT);
// TransIT lets the admin choose the allowed payment methods, GlobalPay only supports credit card
export const IS_PAYMENT_METHOD_SELECTABLE = Boolean(IS_TRANSIT_PAYMENT);
export const AVAILABLE_PAYMENT_METHODS: readonly PaymentMethod[] = IS_PAYMENT_METHOD_SELECTABLE
  ? PAYMENT_METHODS
  : ['CreditCard'];

export enum SettingsFormKeys {
  CONTACT_EMAIL = 'contactEmail',
  SUBSCRIPTION_REQUEST_EMAIL = 'subscriptionRequestEmail',
  IS_PAYMENT_ENABLED = 'isPaymentEnabled',
  SURCHARGE_PERCENTAGE = 'surchargePercentage',
  IS_PAYMENT_CUTOFF_ENABLED = 'isPaymentCutoffEnabled',
  MAX_ALLOWED_DAYS_TO_PAY = 'maxAllowedDaysToPay',
  RECONCILIATION_EMAILS = 'reconciliationEmails',
  ALLOWED_PAYMENT_METHODS = 'allowedPaymentMethods',
  ACH_LIMIT = 'achLimit',
  CREDIT_CARD_LIMIT = 'creditCardLimit',
  AGING_BUCKETS = 'agingBuckets',
  AGING_DATE_BASIS = 'agingDateBasis',
  SHOW_INVOICES = 'showInvoices',
  SHOW_DELIVERY_NOTES = 'showDeliveryNotes',
  SHOW_ACCOUNT_BALANCE = 'showAccountBalance',
  IS_INVOICE_NOTIFICATION_ENABLED = 'isInvoiceNotificationEnabled',
  INVITATION_REPLY_TO_EMAIL = 'invitationReplyToEmail',
  INVITATION_SHOW_USER_NAME = 'invitationShowUserName',
}

export const PAYMENT_METHOD_LIMIT_KEYS: Record<
  PaymentMethod,
  SettingsFormKeys.ACH_LIMIT | SettingsFormKeys.CREDIT_CARD_LIMIT
> = {
  ACH: SettingsFormKeys.ACH_LIMIT,
  CreditCard: SettingsFormKeys.CREDIT_CARD_LIMIT,
};

export type SettingsInformation = {
  [SettingsFormKeys.CONTACT_EMAIL]: string;
  [SettingsFormKeys.SUBSCRIPTION_REQUEST_EMAIL]: string;
  [SettingsFormKeys.IS_PAYMENT_ENABLED]: boolean;
  [SettingsFormKeys.SURCHARGE_PERCENTAGE]: number | '';
  [SettingsFormKeys.IS_PAYMENT_CUTOFF_ENABLED]: boolean;
  [SettingsFormKeys.MAX_ALLOWED_DAYS_TO_PAY]: number | '';
  [SettingsFormKeys.RECONCILIATION_EMAILS]: string[];
  [SettingsFormKeys.ALLOWED_PAYMENT_METHODS]: PaymentMethod[];
  [SettingsFormKeys.ACH_LIMIT]: number | '';
  [SettingsFormKeys.CREDIT_CARD_LIMIT]: number | '';
  [SettingsFormKeys.AGING_BUCKETS]: number[];
  [SettingsFormKeys.AGING_DATE_BASIS]: AgingDateBasis;
  [SettingsFormKeys.SHOW_INVOICES]: boolean;
  [SettingsFormKeys.SHOW_DELIVERY_NOTES]: boolean;
  [SettingsFormKeys.SHOW_ACCOUNT_BALANCE]: boolean;
  [SettingsFormKeys.IS_INVOICE_NOTIFICATION_ENABLED]: boolean;
  [SettingsFormKeys.INVITATION_REPLY_TO_EMAIL]: string;
  [SettingsFormKeys.INVITATION_SHOW_USER_NAME]: boolean;
};

export const DEFAULT_VALUES: SettingsInformation = {
  [SettingsFormKeys.CONTACT_EMAIL]: '',
  [SettingsFormKeys.SUBSCRIPTION_REQUEST_EMAIL]: '',
  [SettingsFormKeys.IS_PAYMENT_ENABLED]: false,
  [SettingsFormKeys.SURCHARGE_PERCENTAGE]: 0,
  [SettingsFormKeys.IS_PAYMENT_CUTOFF_ENABLED]: false,
  [SettingsFormKeys.MAX_ALLOWED_DAYS_TO_PAY]: 0,
  [SettingsFormKeys.RECONCILIATION_EMAILS]: [],
  [SettingsFormKeys.ALLOWED_PAYMENT_METHODS]: IS_PAYMENT_METHOD_SELECTABLE
    ? []
    : [...AVAILABLE_PAYMENT_METHODS],
  [SettingsFormKeys.ACH_LIMIT]: DEFAULT_PAYMENT_LIMITS.ACH,
  [SettingsFormKeys.CREDIT_CARD_LIMIT]: DEFAULT_PAYMENT_LIMITS.CreditCard,
  [SettingsFormKeys.AGING_BUCKETS]: DEFAULT_AGING_BUCKETS,
  [SettingsFormKeys.AGING_DATE_BASIS]: DEFAULT_AGING_DATE_BASIS,
  [SettingsFormKeys.SHOW_INVOICES]: true,
  [SettingsFormKeys.SHOW_DELIVERY_NOTES]: true,
  [SettingsFormKeys.SHOW_ACCOUNT_BALANCE]: true,
  [SettingsFormKeys.IS_INVOICE_NOTIFICATION_ENABLED]: true,
  [SettingsFormKeys.INVITATION_REPLY_TO_EMAIL]: '',
  [SettingsFormKeys.INVITATION_SHOW_USER_NAME]: false,
};

export const isValidEmail = (email: string): boolean =>
  yup.string().email().required().isValidSync(email);

export const isPositiveInteger = (value: string): boolean => /^[1-9]\d*$/.test(value.trim());

const getContactFormValues = (config: BaseApiLegalCorporationConfig) => ({
  [SettingsFormKeys.CONTACT_EMAIL]: config.contactEmail ?? '',
  [SettingsFormKeys.SUBSCRIPTION_REQUEST_EMAIL]: config.subscriptionRequestEmail ?? '',
});

const getPaymentSettingsFormValues = (config: BaseApiLegalCorporationConfig) => ({
  [SettingsFormKeys.IS_PAYMENT_ENABLED]: config.isPaymentEnabled ?? false,
  [SettingsFormKeys.SURCHARGE_PERCENTAGE]: Number(config.surchargePercentage ?? 0),
  [SettingsFormKeys.IS_PAYMENT_CUTOFF_ENABLED]: config.isPaymentCutoffEnabled ?? false,
  [SettingsFormKeys.MAX_ALLOWED_DAYS_TO_PAY]: config.maxAllowedDaysToPay ?? 0,
  [SettingsFormKeys.RECONCILIATION_EMAILS]: (config.reconciliationEmails ?? '')
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean),
});

const getPaymentConfigurationFormValues = (config: BaseApiLegalCorporationConfig) => {
  const paymentLimits = config.paymentConfiguration?.payment_limits ?? {};
  return {
    [SettingsFormKeys.ALLOWED_PAYMENT_METHODS]: IS_PAYMENT_METHOD_SELECTABLE
      ? (config.paymentConfiguration?.allowed_payment_methods ?? []).filter(
          (method): method is PaymentMethod => PAYMENT_METHODS.includes(method as PaymentMethod),
        )
      : [...AVAILABLE_PAYMENT_METHODS],
    [SettingsFormKeys.ACH_LIMIT]: paymentLimits.ACH ?? DEFAULT_PAYMENT_LIMITS.ACH,
    [SettingsFormKeys.CREDIT_CARD_LIMIT]:
      paymentLimits.CreditCard ?? DEFAULT_PAYMENT_LIMITS.CreditCard,
  };
};

const getAccountBalanceFormValues = (config: BaseApiLegalCorporationConfig) => {
  const agingDateBasis = config.accountBalanceConfiguration?.aging_date_basis;
  const agingBuckets = config.accountBalanceConfiguration?.aging_buckets ?? [];
  return {
    [SettingsFormKeys.AGING_BUCKETS]:
      agingBuckets.length > 0 ? agingBuckets.map(Number) : DEFAULT_AGING_BUCKETS,
    [SettingsFormKeys.AGING_DATE_BASIS]: AGING_DATE_BASIS.includes(agingDateBasis as AgingDateBasis)
      ? (agingDateBasis as AgingDateBasis)
      : DEFAULT_AGING_DATE_BASIS,
  };
};

const getFeatureFlagFormValues = (config: BaseApiLegalCorporationConfig) => ({
  [SettingsFormKeys.SHOW_INVOICES]: config.tabConfiguration?.show_invoices ?? true,
  [SettingsFormKeys.SHOW_DELIVERY_NOTES]: config.tabConfiguration?.show_delivery_notes ?? true,
  [SettingsFormKeys.SHOW_ACCOUNT_BALANCE]: config.tabConfiguration?.show_account_balance ?? true,
  [SettingsFormKeys.IS_INVOICE_NOTIFICATION_ENABLED]: config.isInvoiceNotificationEnabled ?? true,
  [SettingsFormKeys.INVITATION_REPLY_TO_EMAIL]:
    config.invitationEmailConfiguration?.reply_to_email ?? '',
  [SettingsFormKeys.INVITATION_SHOW_USER_NAME]:
    config.invitationEmailConfiguration?.show_user_name ?? false,
});

export const toFormValues = (config: BaseApiLegalCorporationConfig | null): SettingsInformation => {
  if (!config) {
    return DEFAULT_VALUES;
  }
  return {
    ...DEFAULT_VALUES,
    ...getContactFormValues(config),
    ...getPaymentSettingsFormValues(config),
    ...getPaymentConfigurationFormValues(config),
    ...getAccountBalanceFormValues(config),
    ...getFeatureFlagFormValues(config),
  };
};

const toNumber = (value: number | '', fallback: number): number =>
  value === '' || Number.isNaN(Number(value)) ? fallback : Number(value);

export const toRequestBody = (
  values: SettingsInformation,
  legalCorporationId: string,
  initialValues: SettingsInformation = DEFAULT_VALUES,
): UpdateSettingsRequestBody => {
  const paymentLimits: UpdateSettingsRequestBody['paymentLimits'] = {};
  values[SettingsFormKeys.ALLOWED_PAYMENT_METHODS].forEach((method) => {
    paymentLimits[method] = Number(values[PAYMENT_METHOD_LIMIT_KEYS[method]]);
  });

  return {
    legalCorporationId,
    contactEmail: values[SettingsFormKeys.CONTACT_EMAIL].trim(),
    subscriptionRequestEmail: values[SettingsFormKeys.SUBSCRIPTION_REQUEST_EMAIL].trim(),
    isPaymentEnabled: values[SettingsFormKeys.IS_PAYMENT_ENABLED],
    surchargePercentage: toNumber(
      values[SettingsFormKeys.SURCHARGE_PERCENTAGE],
      toNumber(initialValues[SettingsFormKeys.SURCHARGE_PERCENTAGE], 0),
    ),
    isPaymentCutoffEnabled: values[SettingsFormKeys.IS_PAYMENT_CUTOFF_ENABLED],
    maxAllowedDaysToPay: toNumber(
      values[SettingsFormKeys.MAX_ALLOWED_DAYS_TO_PAY],
      toNumber(initialValues[SettingsFormKeys.MAX_ALLOWED_DAYS_TO_PAY], 0),
    ),
    reconciliationEmails: values[SettingsFormKeys.RECONCILIATION_EMAILS],
    allowedPaymentMethods: values[SettingsFormKeys.ALLOWED_PAYMENT_METHODS],
    paymentLimits,
    agingBuckets: [...values[SettingsFormKeys.AGING_BUCKETS]].sort((a, b) => a - b),
    agingDateBasis: values[SettingsFormKeys.AGING_DATE_BASIS],
    showInvoices: values[SettingsFormKeys.SHOW_INVOICES],
    showDeliveryNotes: values[SettingsFormKeys.SHOW_DELIVERY_NOTES],
    showAccountBalance: values[SettingsFormKeys.SHOW_ACCOUNT_BALANCE],
    isInvoiceNotificationEnabled: values[SettingsFormKeys.IS_INVOICE_NOTIFICATION_ENABLED],
    invitationReplyToEmail: values[SettingsFormKeys.INVITATION_REPLY_TO_EMAIL].trim(),
    invitationShowUserName: values[SettingsFormKeys.INVITATION_SHOW_USER_NAME],
  };
};
